'use client'

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import { createClient } from '@/utils/supabase/client'
import { getUserAccessToken } from '@/app/(main)/agency/(auth)/helper'
import { getUserDataRegion } from '@/lib/auth-service'
import { getUserFromLocalStorage } from '@/utils/storage'
import { getRegionBasedWebsocketUrl } from '@/utils/urls'

export interface WebSocketMessage {
  type:
    | 'auth'
    | 'auth_status'
    | 'error'
    | 'ping'
    | 'pong'
    | 'subscribe'
    | 'domain_status'
    | 'credit_alert'
    | 'credit_usage'
    | 'credit_log'
    | 'credit_warning'
    | 'get_credit_usage'
  payload: any
  timestamp?: string
  error?: string
}

type MessageHandler = (message: WebSocketMessage) => void

interface WebSocketContextType {
  sendMessage: (message: WebSocketMessage) => void
  isConnected: boolean
  addMessageHandler: (handler: MessageHandler) => void
  removeMessageHandler: (handler: MessageHandler) => void
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(
  undefined
)

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const wsRef = useRef<WebSocket | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const messageHandlersRef = useRef<Set<MessageHandler>>(new Set())
  const pingIntervalRef = useRef<any>(null)
  const reconnectAttemptsRef = useRef(0)
  const mountedRef = useRef(true)
  const MAX_RECONNECT_ATTEMPTS = 5

  const connect = useCallback(async () => {
    const userInfoString = localStorage.getItem('user')
    if (!userInfoString) return

    const token = (() => {
      try {
        return JSON.parse(userInfoString)?.userInfo?.session?.access_token
      } catch (err) {
        console.error('[WS] Failed to parse token:', err)
        return null
      }
    })()

    if (!token) {
      console.warn('[WS] No token found, abort connect')
      return
    }

    const user = getUserFromLocalStorage()
    const email = user?.userInfo.user.email
    const region = await getUserDataRegion(email ?? '')
    const wsUrl = getRegionBasedWebsocketUrl(region ?? '')

    const ws = new WebSocket(wsUrl)
    wsRef.current = ws

    const authMessage: WebSocketMessage = {
      type: 'auth',
      payload: { token },
      timestamp: new Date().toISOString()
    }

    const authTimeout = setTimeout(() => {
      console.warn('[WS] auth_status timeout — force close')
      ws.close()
    }, 10000)

    ws.onopen = () => {
      ws.send(JSON.stringify(authMessage))
    }

    ws.onmessage = event => {
      clearTimeout(authTimeout)
      try {
        const message: WebSocketMessage = JSON.parse(event.data)

        if (message.type === 'auth_status') {
          if (message.payload?.is_authenticated) {
            setIsConnected(true)
            reconnectAttemptsRef.current = 0

            if (pingIntervalRef.current) clearInterval(pingIntervalRef.current)
            pingIntervalRef.current = setInterval(() => {
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(
                  JSON.stringify({
                    type: 'ping',
                    payload: {},
                    timestamp: new Date().toISOString()
                  })
                )
              }
            }, 25000)
          } else {
            console.error('[WS] ❌ Auth failed:', message.payload)
            ws.close()
          }
        }

        messageHandlersRef.current.forEach(handler => {
          try {
            handler(message)
          } catch (err) {
            console.error('[WS] Handler error:', err)
          }
        })
      } catch (err) {
        console.error('[WS] Failed to parse message:', event.data)
      }
    }

    ws.onclose = event => {
      setIsConnected(false)
      clearTimeout(authTimeout)
      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        reconnectAttemptsRef.current++
        setTimeout(() => {
          if (mountedRef.current) connect()
        }, 2000 * reconnectAttemptsRef.current)
      }
    }

    ws.onerror = error => {
      setIsConnected(false)
      console.error('[WS] WebSocket error:', error)
    }
  }, [])

  // ✅ Start connection on login
  useEffect(() => {
    let interval: any = null

    function hasValidToken(): boolean {
      try {
        const userInfo = JSON.parse(localStorage.getItem('user') || '{}')
        return (
          !!userInfo?.userInfo?.session?.access_token &&
          !!userInfo?.userInfo?.user?.agencyId
        )
      } catch {
        return false
      }
    }

    if (!hasValidToken()) {
      interval = setInterval(() => {
        if (hasValidToken()) {
          clearInterval(interval)
          connect()
        }
      }, 1000)
    } else {
      connect()
    }

    return () => {
      clearInterval(interval)
      wsRef.current?.close()
      clearInterval(pingIntervalRef.current)
    }
  }, [connect])

  const sendMessage = useCallback((message: WebSocketMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const msg = { ...message, timestamp: new Date().toISOString() }
      wsRef.current.send(JSON.stringify(msg))
    }
  }, [])

  const addMessageHandler = useCallback((handler: MessageHandler) => {
    messageHandlersRef.current.add(handler)
  }, [])

  const removeMessageHandler = useCallback((handler: MessageHandler) => {
    messageHandlersRef.current.delete(handler)
  }, [])

  return (
    <WebSocketContext.Provider
      value={{
        sendMessage,
        isConnected,
        addMessageHandler,
        removeMessageHandler
      }}
    >
      {children}
    </WebSocketContext.Provider>
  )
}

export const useWebSocket = () => {
  const context = useContext(WebSocketContext)
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider')
  }
  return context
}
